import { propEq } from 'ramda'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'
import AreaTitle from '@/components/area-title'
import { Button } from '@/components/button'
import ContentWrapper from '@/components/content-wrapper'
import { SwitchToggleControlled } from '@/components/form'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import { DefaultButtonBar } from '@/components/button-bar'
import { colors } from '@/styles'
import { showHideListing } from '@/features/SponsoredListing/modals/hide-listing-modal'

const StatusText = styled.p`
  color: ${colors.grey24};
  text-align: center;
  width: fit-content;
  background-color: ${colors.blue96};
  margin: 0 auto;
  padding: 8px 12px;
  border-radius: 8px;

  span {
    font-weight: 700;
  }
`

const SwitchToggleContainer = styled.div`
  padding: 0 0 25px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    margin-right: 5px;
  }
`

const isHidden = propEq('is_hidden', '1')

const Settings = ({ data, listingData, handleBack, history }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [hidden, setHidden] = useState(isHidden(data))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const toggleHidden = () => {
    if (hidden === false) {
      showHideListing({
        handleToggleHidden: () => setHidden(true),
        onClose: () => queryClient.invalidateQueries({ queryKey: ['sponsored-listings'] }),
        client: queryClient,
        onlyWarning: true
      })
    } else {
      setHidden(false)
    }
  }

  const handleSave = async () => {
    setIsSubmitting(true)
    const formattedListingData = {
      listing_id: data.listing_id,
      min_profile_score: null,
      is_hidden: hidden ? 1 : 0
    }
    await dispatch.listing.update(formattedListingData)
    setIsSubmitting(false)
    history.replace(`/listing/${data.listing_id}`)
  }

  const statusText = hidden ? (
    <Trans i18nKey="l.listing.edit.listing_status_hidden.text">
      This listing <span>cannot</span> be searched by renters
    </Trans>
  ) : (
    <Trans i18nKey="l.listing.edit.listing_status_published.text">
      This listing <span>can</span> be searched by renters
    </Trans>
  )

  if (!data || !listingData) return null

  const listingAddress = `${data.unit_number ? `${data.unit_number} - ` : ''}${
    data.building.full_street_name
  }`

  return (
    <Page>
      <ContentWrapper className="mb-50">
        <Helmet>
          <title>{t('l.listing.edit.edit_listing.title', 'Edit Listing')}</title>
        </Helmet>
        <PageTitle>{listingAddress}</PageTitle>
        <AreaTitle>{t('l.listing.edit.listing_status.subtitle', 'Listing Status')}</AreaTitle>
        <Panel vSpace={30} hSpace={30}>
          <SwitchToggleContainer>
            <p>{t('l.listing.edit.settings.published.label', 'Published')}</p>
            <SwitchToggleControlled enabled={!hidden} onChange={toggleHidden} />
          </SwitchToggleContainer>
          <StatusText>{statusText}</StatusText>
        </Panel>
      </ContentWrapper>
      <DefaultButtonBar handleBack={handleBack}>
        <Button
          theme="primary"
          size="small"
          type="submit"
          disabled={isSubmitting}
          onClick={handleSave}
        >
          {t('l.listing.edit.update.button', 'Update')}
        </Button>
      </DefaultButtonBar>
    </Page>
  )
}

export default withRouter(Settings)
